/**
 * ChipRow is a closed set of choices as a row of pills.
 *
 * The owner rejects a native `<select>` in the studio: on a phone it is a
 * modal wheel that hides the other options behind a tap, and every choice here
 * is short enough to show whole. The picture sheet's shape picker was the first
 * of these; the quality row (ADR-0086) is the second, which is what makes one
 * component worth having rather than a third hand-rolled copy.
 *
 * What it does *not* own: the labels, which are each caller's to translate, and
 * the set of options, which for prices comes from the server. It holds the
 * pressed state, the keyboard affordance and the look.
 */
export default function ChipRow<T extends string>({
  legend,
  options,
  value,
  onChange,
  disabled = false,
}: {
  /** Names the group for assistive technology. Never shown. */
  legend: string;
  options: {
    value: T;
    label: string;
    /** A second line inside the chip — a price, a shape's proportions. */
    note?: string;
    testID: string;
  }[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
}) {
  return (
    <fieldset className="flex flex-wrap items-center gap-1.5">
      <legend className="sr-only">{legend}</legend>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          disabled={disabled}
          aria-pressed={value === option.value}
          data-testid={option.testID}
          onClick={() => onChange(option.value)}
          className={`rounded-full border px-2.5 py-1 text-xs transition-colors disabled:opacity-40 ${
            value === option.value
              ? "border-sage bg-sage-soft font-medium text-sage-deep"
              : "border-line text-muted hover:text-ink"
          }`}
        >
          {option.label}
          {option.note && (
            /* The amount is isolated left to right inside a line that may be
               laid out right to left (05 §Web client). */
            <span className="ms-1 font-normal opacity-70">
              <bdi dir="ltr">{option.note}</bdi>
            </span>
          )}
        </button>
      ))}
    </fieldset>
  );
}
